import { useState } from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { useTheme } from '@mui/material/styles';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { getSurfaceBorder, getSurfaceColor, radii } from '../../theme/tokens';
import { useUpsAuthContext } from './UpsAuthContext';

// Single shared auth UI for both entry points — reads the same
// UpsAuthProvider context wherever it's rendered, so there is exactly one
// token state regardless of which page mounts this. `embedded` affects only
// spacing/margins, never copy or auth logic.
export const UpsAuthPanel = ({ embedded = false }: { embedded?: boolean }) => {
  const theme = useTheme();
  const { isAuthorized, authorize, clear } = useUpsAuthContext();
  const [open, setOpen] = useState(false);
  const [value, setValue] = useState('');

  const submit = () => {
    if (!value.trim()) return;
    authorize(value);
    setValue('');
    setOpen(false);
  };

  const surfaceSx = {
    backgroundColor: getSurfaceColor(theme, 'raised'),
    border: `1px solid ${getSurfaceBorder(theme)}`,
    borderRadius: `${radii.lg}px`,
    px: 2,
    py: 1.25,
    ...(embedded ? { mb: 1.5 } : {}),
  };

  if (isAuthorized) {
    return (
      <Stack alignItems="center" direction="row" justifyContent="space-between" sx={surfaceSx}>
        <Stack alignItems="center" direction="row" spacing={1}>
          <CheckCircleIcon sx={{ color: theme.palette.success.main, fontSize: 18 }} />
          <Typography sx={{ fontSize: 13, fontWeight: 600 }}>Unified Profiles connected</Typography>
        </Stack>
        <Button onClick={clear} size="small">
          Clear authorization
        </Button>
      </Stack>
    );
  }

  return (
    <>
      <Stack alignItems="center" direction="row" justifyContent="space-between" spacing={2} sx={surfaceSx}>
        <Box>
          <Typography sx={{ fontSize: 13, fontWeight: 700 }}>Unified Profiles access</Typography>
          <Typography sx={{ color: theme.palette.text.secondary, fontSize: 12.5, mt: 0.25 }}>
            Authorize with your UPS bearer token to search live profile data. Your token is kept in
            memory for this session and is not stored by Backstage.
          </Typography>
        </Box>
        <Button onClick={() => setOpen(true)} size="small" sx={{ flexShrink: 0 }} variant="contained">
          Authorize UPS
        </Button>
      </Stack>

      <Dialog onClose={() => setOpen(false)} open={open}>
        <DialogTitle>Authorize UPS</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            fullWidth
            label="UPS bearer token"
            onChange={e => setValue(e.target.value)}
            onKeyDown={e => {
              if (e.key === 'Enter') submit();
            }}
            sx={{ mt: 1, minWidth: 360 }}
            type="password"
            value={value}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)}>Cancel</Button>
          <Button disabled={!value.trim()} onClick={submit} variant="contained">
            Authorize
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};
